import React, { useCallback, useEffect, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  deleteSbatGroupCommunicationsSelector,
  deleteReasonSelector,
  DELETE_CLASS_MODAL_STAGES,
  DELETE_CLASS_REASON_OPTIONS,
  selectedDeleteSbatGroupSelector,
  setDeleteClassModalStage,
  setDeleteReason,
  setDeleteSbatGroupCommunications,
  setDeleteSbatGroupErrorMessage,
  useValidateDestroyMutation,
} from 'features/classCreation';
import GlobalLoader from 'components/GlobalLoader';
import SectionHeading from 'components/SectionHeading';
import Select from 'components/Select';
import SelectNotificationMode from 'components/SelectNotificationMode';
import styles from './DeleteClassModalReasonStage.module.scss';
import toDDMMMYYYYHHMM from 'utils/toDDMMMYYYYHHMM';

export default function DeleteClassModalReasonStage() {
  const dispatch = useDispatch();
  const communications = useSelector(deleteSbatGroupCommunicationsSelector);
  const selectedDeleteReason = useSelector(deleteReasonSelector);
  const sbatGroup = useSelector(selectedDeleteSbatGroupSelector);
  const [validateDestroy, { isLoading, data, error }] =
    useValidateDestroyMutation();

  const deleteWithComms = useMemo(
    () => Object.values(communications).some((val) => val),
    [communications]
  );

  const handleSend = useCallback(() => {
    validateDestroy({ sbatGroupIds: [sbatGroup.id] });
  }, [sbatGroup.id, validateDestroy]);

  useEffect(() => {
    if (data?.success) {
      dispatch(setDeleteClassModalStage(DELETE_CLASS_MODAL_STAGES.confirm));
    } else if (data?.success === false) {
      batch(() => {
        dispatch(
          setDeleteClassModalStage(DELETE_CLASS_MODAL_STAGES.validation)
        );
        dispatch(setDeleteSbatGroupErrorMessage(data?.message));
      });
    }
  }, [dispatch, data]);

  const handleReasonChange = useCallback(
    (option) => {
      dispatch(setDeleteReason(option));
    },
    [dispatch]
  );

  const handleCommsToggle = useCallback(
    (commType) => {
      dispatch(
        setDeleteSbatGroupCommunications({
          channel: commType,
          enabled: !communications[commType],
        })
      );
    },
    [communications, dispatch]
  );

  useEffect(() => {
    if (error) {
      cogoToast.error(DEFAULT_ERROR_MESSAGE);
    }
  }, [error]);

  return (
    <>
      <GlobalLoader isVisible={isLoading} />
      <SectionHeading
        title="Delete Class"
        titleClassName={styles.heading}
        className={styles.sectionHeading}
        subtitle="Fill in the following details to send comms regarding class updation"
      />
      <div className="dark bold h5">Class Details</div>
      <div className="h6">
        <div className="row">
          <div className="dark m-r-xxs">Date and Time:</div>
          {toDDMMMYYYYHHMM(sbatGroup.date_of_topic)}
        </div>
        <div className="row">
          <div className="dark m-r-xxs">Topic:</div>
          {sbatGroup.academy_topic.title}
        </div>
        <div className="row">
          <div className="dark m-r-xxs">Batch/es:</div>
          {sbatGroup.super_batch_academy_topics
            .map((sbat) => sbat.super_batch.name)
            .join(', ')}
        </div>
      </div>
      <Select
        className="m-t-s"
        placeholder="Select Reason For Class Deletion"
        options={DELETE_CLASS_REASON_OPTIONS}
        value={selectedDeleteReason}
        onChange={handleReasonChange}
      />
      <SelectNotificationMode
        communications={communications}
        handleCommsToggle={handleCommsToggle}
      />
      <Tappable
        className="btn btn-primary btn-long m-t-l h4"
        onClick={handleSend}
      >
        {deleteWithComms
          ? 'Delete Class and Send Notifications'
          : 'Delete Class without Sending Notifications'}
      </Tappable>
    </>
  );
}
